"use client";

/**
 * Per-enrollment detail drawer (Phase J · Task 58).
 *
 * Right-hand `Sheet` opened by clicking a row in the Enrollments tab.
 * Sections (per spec §13.1 `detail-drawer.tsx` + §10.2):
 *
 *   1. Candidate profile (avatar + name + headline + resolved contact).
 *   2. Sequence pin (version label + link to the step_set version).
 *   3. Step history — synthetic timeline derived from
 *      `current_step_index` against the sequence's current step set.
 *      Each step renders as `done` (index < current),
 *      `current` (index === current and the row is active/paused), or
 *      `upcoming` (index > current). Terminal rows mark all remaining
 *      steps as `skipped`.
 *   4. Inbox link — placeholder until sub-project 06 ships the
 *      sequence inbox surface.
 *   5. Audit log — client-filtered slice of `useListAuditEvents` to
 *      events whose action starts with `outreach.enrollment.` and
 *      whose `target_summary` references this enrollment id.
 *
 * The drawer mounts both `useGetEnrollment` (for the resolved contact
 * fields and the pin metadata) and `useGetProfile` (for the candidate
 * card). Network calls only fire when `open` is true so closed
 * drawers cost nothing.
 */

import { useMemo } from "react";

import { formatDistanceToNow } from "date-fns";

import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Icon } from "@/components/ui/icon";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useGetEnrollment,
  useGetProfile,
  useGetSequence,
  useListAuditEvents,
} from "@/lib/api/generated";
import type { AuditEventResponse } from "@/lib/api/model/auditEventResponse";
import { cn } from "@/lib/utils";

import { ENROLLMENT_STATUS_META } from "../../lib/status-meta";
import type { EnrollmentListItem, EnrollmentStatus } from "../../lib/types";

const STEP_TYPE_LABEL: Record<string, string> = {
  automatic_email: "Auto Email",
  manual_email: "Manual Email",
  phone_call: "Phone Call",
  text_message: "Text Message",
  custom_task: "Custom Task",
  linkedin_profile_view: "Social View",
  linkedin_connection_request: "Social Connect",
  linkedin_message: "Social Message",
};

interface EnrollmentDetailDrawerProps {
  enrollment: EnrollmentListItem | null;
  jobId: string;
  sequenceId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function EnrollmentDetailDrawer(props: EnrollmentDetailDrawerProps) {
  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange} size="md">
      <SheetHeader>
        <div className="flex items-center gap-1.5">
          <Icon name="IconTeam" size={16} className="text-heading opacity-70" />
          <SheetTitle>Enrollment</SheetTitle>
        </div>
        <SheetClose aria-label="Close enrollment drawer" />
      </SheetHeader>
      <SheetBody className="!gap-6 !p-0">
        {props.open && props.enrollment ? (
          <DrawerBody {...props} enrollment={props.enrollment} />
        ) : null}
      </SheetBody>
    </Sheet>
  );
}

interface DrawerBodyProps extends EnrollmentDetailDrawerProps {
  enrollment: EnrollmentListItem;
}

function DrawerBody({ enrollment, jobId, sequenceId, open }: DrawerBodyProps) {
  const detail = useGetEnrollment(jobId, sequenceId, enrollment.id, {
    query: { enabled: open },
  });
  const profile = useGetProfile(enrollment.profile_id, undefined, {
    query: {
      enabled: open && Boolean(enrollment.profile_id),
      staleTime: 60_000,
    },
  });
  const sequence = useGetSequence(jobId, sequenceId, {
    query: { enabled: open },
  });
  const audit = useEnrollmentAuditEvents({
    enrollmentId: enrollment.id,
    enabled: open,
  });

  return (
    <div className="flex flex-col">
      <ProfileSection
        profile={profile.data}
        resolvedEmail={
          detail.data?.resolved_email ?? enrollment.resolved_email ?? null
        }
        status={enrollment.status}
      />
      <SectionDivider />
      <PinSection
        pinnedStepSetId={detail.data?.pinned_step_set_id ?? null}
        sequenceCurrentStepSetId={sequence.data?.current_step_set_id ?? null}
        currentVersion={sequence.data?.current_step_set?.version_number ?? null}
      />
      <SectionDivider />
      <StepHistorySection
        steps={sequence.data?.current_step_set?.steps ?? []}
        currentStepIndex={enrollment.current_step_index}
        status={enrollment.status}
      />
      <SectionDivider />
      <InboxLinkSection enrollmentId={enrollment.id} />
      <SectionDivider />
      <AuditSection
        events={audit.events}
        isLoading={audit.isLoading}
        isError={audit.isError}
      />
    </div>
  );
}

function SectionDivider() {
  return <div className="border-t border-border" />;
}

interface ProfileSectionProps {
  profile?: {
    full_name?: string | null;
    headline?: string | null;
    job_title?: string | null;
    job_company_name?: string | null;
    avatar_url?: string | null;
  } | null;
  resolvedEmail: string | null;
  status: EnrollmentStatus;
}

function ProfileSection({
  profile,
  resolvedEmail,
  status,
}: ProfileSectionProps) {
  const fullName = profile?.full_name?.trim() || "Unknown candidate";
  const headline =
    profile?.headline?.trim() ||
    profile?.job_title?.trim() ||
    profile?.job_company_name?.trim() ||
    null;
  const meta = ENROLLMENT_STATUS_META[status];
  return (
    <section
      data-testid="enrollment-drawer-profile"
      className="flex items-start gap-4 px-4 py-5 sm:px-6"
    >
      <CandidateAvatar
        name={fullName}
        avatarUrl={profile?.avatar_url ?? null}
        size={48}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h2 className="truncate text-base font-medium text-heading">
          {fullName}
        </h2>
        {headline && (
          <p className="truncate text-sm text-muted-foreground">{headline}</p>
        )}
        <div className="mt-1 flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full bg-white/4 px-2 py-0.5 text-xs font-medium",
              meta.color,
            )}
          >
            <Icon name={meta.icon} size={12} />
            <span>{meta.label}</span>
          </span>
          {resolvedEmail && (
            <span
              className="truncate text-xs text-muted-foreground"
              title={resolvedEmail}
            >
              {resolvedEmail}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

interface PinSectionProps {
  pinnedStepSetId: string | null;
  sequenceCurrentStepSetId: string | null;
  currentVersion: number | null;
}

function PinSection({
  pinnedStepSetId,
  sequenceCurrentStepSetId,
  currentVersion,
}: PinSectionProps) {
  const pinMatchesCurrent = Boolean(
    pinnedStepSetId && pinnedStepSetId === sequenceCurrentStepSetId,
  );

  return (
    <section
      data-testid="enrollment-drawer-pin"
      className="flex flex-col gap-1.5 px-4 py-4 sm:px-6"
    >
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Sequence pin
      </h3>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-white/4 px-2 py-0.5 text-xs font-medium text-foreground">
          <Icon name="IconHistory" size={12} />
          {pinMatchesCurrent && currentVersion !== null
            ? `Pinned to v${currentVersion} (current)`
            : currentVersion !== null
              ? `Pinned to an older version (current is v${currentVersion})`
              : "Pinned step set"}
        </span>
        {!pinMatchesCurrent && (
          <span className="text-xs text-muted-foreground">
            Restart re-pins to the current version.
          </span>
        )}
      </div>
    </section>
  );
}

type SyntheticStepStatus = "done" | "current" | "upcoming" | "skipped";

interface StepHistorySectionProps {
  steps: Array<{ id: string; type: string; step_index: number }>;
  currentStepIndex: number;
  status: EnrollmentStatus;
}

function StepHistorySection({
  steps,
  currentStepIndex,
  status,
}: StepHistorySectionProps) {
  const isTerminal = ENROLLMENT_STATUS_META[status].is_terminal;
  return (
    <section
      data-testid="enrollment-drawer-history"
      className="flex flex-col gap-2 px-4 py-4 sm:px-6"
    >
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Step history
      </h3>
      {steps.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          The sequence currently has no steps to show.
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {steps.map((step, index) => {
            const stepStatus = resolveStepStatus(
              index,
              currentStepIndex,
              status,
              isTerminal,
            );
            return (
              <StepRow
                key={step.id}
                index={index}
                stepStatus={stepStatus}
                typeLabel={STEP_TYPE_LABEL[step.type] ?? step.type}
              />
            );
          })}
        </ol>
      )}
    </section>
  );
}

function resolveStepStatus(
  index: number,
  currentIndex: number,
  status: EnrollmentStatus,
  isTerminal: boolean,
): SyntheticStepStatus {
  if (status === "completed") {
    return index < currentIndex ? "done" : "done";
  }
  if (isTerminal) {
    if (index < currentIndex) return "done";
    if (index === currentIndex) return "skipped";
    return "skipped";
  }
  if (index < currentIndex) return "done";
  if (index === currentIndex) return "current";
  return "upcoming";
}

function StepRow({
  index,
  stepStatus,
  typeLabel,
}: {
  index: number;
  stepStatus: SyntheticStepStatus;
  typeLabel: string;
}) {
  const meta: Record<
    SyntheticStepStatus,
    {
      icon: React.ComponentProps<typeof Icon>["name"];
      tone: string;
      label: string;
    }
  > = {
    done: { icon: "IconCircleCheck", tone: "text-success", label: "Done" },
    current: { icon: "IconClock", tone: "text-primary", label: "Current" },
    upcoming: {
      icon: "IconCircle",
      tone: "text-muted-foreground",
      label: "Upcoming",
    },
    skipped: {
      icon: "IconCircleX",
      tone: "text-muted-foreground",
      label: "Skipped",
    },
  };
  const m = meta[stepStatus];
  return (
    <li
      data-testid={`enrollment-drawer-step-${index}`}
      data-status={stepStatus}
      className="flex items-center gap-2.5 rounded-md bg-white/2 px-3 py-2"
    >
      <Icon name={m.icon} size={14} className={m.tone} />
      <span className="text-sm text-foreground">
        Step {index + 1}: {typeLabel}
      </span>
      <span className={cn("ml-auto text-xs", m.tone)}>{m.label}</span>
    </li>
  );
}

function InboxLinkSection({ enrollmentId }: { enrollmentId: string }) {
  return (
    <section
      data-testid="enrollment-drawer-inbox"
      className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6"
    >
      <div className="flex flex-col gap-0.5">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Conversation
        </h3>
        <p className="text-sm text-muted-foreground">
          Email + Social thread for this enrollment lands with the sub-project
          06 inbox.
        </p>
      </div>
      <button
        type="button"
        data-testid={`enrollment-drawer-thread-link-${enrollmentId}`}
        disabled
        title="Inbox surface ships with sub-project 06"
        className="inline-flex items-center gap-1.5 rounded-md bg-white/4 px-2.5 py-1 text-xs font-medium text-muted-foreground opacity-60"
      >
        View thread
        <Icon name="IconArrowRight" size={12} />
      </button>
    </section>
  );
}

interface AuditSectionProps {
  events: AuditEventResponse[];
  isLoading: boolean;
  isError: boolean;
}

function AuditSection({ events, isLoading, isError }: AuditSectionProps) {
  return (
    <section
      data-testid="enrollment-drawer-audit"
      className="flex flex-col gap-2 px-4 py-4 sm:px-6"
    >
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Activity
      </h3>
      {isLoading ? (
        <div
          aria-label="Loading activity"
          role="status"
          className="flex flex-col gap-2"
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-md bg-white/4" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load activity for this enrollment.
        </p>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No activity recorded yet.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {events.map((evt) => (
            <AuditRow key={evt.id} event={evt} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AuditRow({ event }: { event: AuditEventResponse }) {
  const action = humanizeEnrollmentAction(event.action);
  const actor = event.actor.name?.trim() || event.actor.email || "Someone";
  const when = formatDistanceToNow(new Date(event.occurred_at), {
    addSuffix: true,
  });
  return (
    <li
      data-testid={`enrollment-drawer-audit-row-${event.id}`}
      className="flex items-start gap-2 py-2.5"
    >
      <Icon
        name="IconHistory"
        size={14}
        className="mt-0.5 text-muted-foreground"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="truncate text-sm">
          <span className="font-medium text-heading">{actor}</span>
          <span className="text-muted-foreground"> {action}</span>
        </p>
        {event.summary && (
          <p className="text-xs text-muted-foreground">{event.summary}</p>
        )}
        <p className="text-xs text-muted-foreground/70">{when}</p>
      </div>
    </li>
  );
}

const ENROLLMENT_ACTION_LABELS: Record<string, string> = {
  "outreach.enrollment.preview_committed": "committed the preview",
  "outreach.enrollment.created": "created the enrollment",
  "outreach.enrollment.batch_committed": "enrolled this candidate",
  "outreach.enrollment.paused": "paused the enrollment",
  "outreach.enrollment.resumed": "resumed the enrollment",
  "outreach.enrollment.cancelled": "cancelled the enrollment",
  "outreach.enrollment.completed": "completed the sequence",
  "outreach.enrollment.failed": "the enrollment errored",
  "outreach.enrollment.override_set": "set a step override",
  "outreach.enrollment.override_cleared": "cleared a step override",
  "outreach.enrollment.restarted": "restarted the enrollment",
};

function humanizeEnrollmentAction(action: string): string {
  if (ENROLLMENT_ACTION_LABELS[action]) return ENROLLMENT_ACTION_LABELS[action];
  return action.replace(/^outreach\.enrollment\./, "").replace(/[._-]/g, " ");
}

interface UseEnrollmentAuditArgs {
  enrollmentId: string;
  enabled: boolean;
}

const ENROLLMENT_ACTION_PREFIX = "outreach.enrollment.";

function useEnrollmentAuditEvents({
  enrollmentId,
  enabled,
}: UseEnrollmentAuditArgs) {
  const query = useListAuditEvents({ per_page: 50 }, { query: { enabled } });

  const events = useMemo<AuditEventResponse[]>(() => {
    if (!enrollmentId) return [];
    const all = query.data?.events ?? [];
    return all.filter((evt) => {
      if (!evt.action.startsWith(ENROLLMENT_ACTION_PREFIX)) return false;
      const target = evt.target_summary ?? "";
      return target.includes(enrollmentId);
    });
  }, [query.data?.events, enrollmentId]);

  return {
    events,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
